import React, { useState, useEffect } from 'react';
import { Search, Eye, Heart, MessageSquare, ExternalLink } from 'lucide-react';
import { rawProjects } from '../data/projectsData';
import { supabase, isSupabaseConfigured } from '../config/supabase';
import ProjectCommentsModal from './modals/ProjectCommentsModal';

function Portfolio({ isAdmin }) {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [liveData, setLiveData] = useState({});
    const [localUserLikes, setLocalUserLikes] = useState({});
    const [activeProjectForComments, setActiveProjectForComments] = useState(null);
    const isOnline = navigator.onLine && isSupabaseConfigured;

    useEffect(() => {
        const storedLikes = JSON.parse(localStorage.getItem('ezems_user_likes') || '{}');
        setLocalUserLikes(storedLikes);
        
        if (isOnline) {
            const fetchData = async () => {
                try {
                    const { data } = await supabase.from('projects').select('*');
                    if (data) {
                        const formatted = {};
                        data.forEach(p => formatted[p.id] = p);
                        setLiveData(formatted);
                    }
                } catch(e) {
                    console.error('Fetch error:', e);
                }
            };
            fetchData();
            
            const channel = supabase
                .channel('realtime_projects')
                .on('postgres_changes', { 
                    event: 'UPDATE', 
                    schema: 'public', 
                    table: 'projects' 
                }, (payload) => {
                    setLiveData(prev => ({ ...prev, [payload.new.id]: payload.new }));
                })
                .subscribe();
            
            return () => supabase.removeChannel(channel);
        }
    }, [isOnline]);

    const getProjectData = (pid) => liveData[pid] || { views: 0, likes: 0, comments: [] };

    const handleLike = async (e, projectId) => {
        e.preventDefault();
        e.stopPropagation();
        
        const isLiked = localUserLikes[projectId];
        const newStatus = !isLiked;
        const updatedUserLikes = { ...localUserLikes, [projectId]: newStatus };
        
        setLocalUserLikes(updatedUserLikes);
        localStorage.setItem('ezems_user_likes', JSON.stringify(updatedUserLikes));
        
        if (isOnline) {
            try {
                const current = liveData[projectId]?.likes || 0;
                const newCount = newStatus ? current + 1 : Math.max(0, current - 1);
                await supabase.from('projects').upsert({ id: projectId, likes: newCount }, { onConflict: 'id' });
            } catch(err) {
                console.error('Like error:', err);
            }
        }
    };

    const handleView = async (projectId) => {
        if (isOnline) {
            await supabase.rpc('increment_stat', { row_id: projectId, col_name: 'views' });
        }
    };

    const handleAddComment = async (projectId, commentData) => {
        const newComment = { 
            ...commentData, 
            date: new Date().toLocaleDateString(), 
            timestamp: Date.now(), 
            likes: 0, 
            replies: [] 
        };
        
        if (isOnline) {
            const currentComments = liveData[projectId]?.comments || [];
            await supabase.from('projects').upsert({ 
                id: projectId, 
                comments: [...currentComments, newComment] 
            });
        }
    };

    const handleDeleteComment = async (projectId, index) => {
        if (isOnline) {
            const currentComments = [...(liveData[projectId]?.comments || [])];
            currentComments.splice(index, 1);
            await supabase.from('projects').upsert({ id: projectId, comments: currentComments });
        }
    };

    const handleLikeComment = async (projectId, commentIdx) => {
        if (isOnline) {
            const currentComments = [...(liveData[projectId]?.comments || [])];
            if (currentComments[commentIdx]) {
                currentComments[commentIdx].likes = (currentComments[commentIdx].likes || 0) + 1;
                await supabase.from('projects').upsert({ id: projectId, comments: currentComments });
            }
        }
    };

    const handleAddReply = async (projectId, commentIdx, replyText) => {
        if (isOnline) {
            const currentComments = [...(liveData[projectId]?.comments || [])];
            if (currentComments[commentIdx]) {
                const newReply = { 
                    name: isAdmin ? "Admin Support" : "User", 
                    text: replyText, 
                    date: "Just now" 
                };
                currentComments[commentIdx].replies = [...(currentComments[commentIdx].replies || []), newReply];
                await supabase.from('projects').upsert({ id: projectId, comments: currentComments });
            }
        }
    };

    const categories = ["All", ...new Set(rawProjects.map(p => p.category))];
    const filteredProjects = rawProjects.filter(project => 
        (project.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
         project.desc.toLowerCase().includes(searchQuery.toLowerCase())) &&
        (selectedCategory === "All" || project.category === selectedCategory)
    );

    return (
        <section id="portfolio" className="py-16 md:py-24 bg-slate-50 relative">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 md:mb-12 gap-6">
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 md:mb-4">Our Ecosystem</h2>
                        <p className="text-sm md:text-base text-slate-500 max-w-xl">
                            Explore the diverse range of platforms we've deployed.
                        </p>
                    </div>
                    
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input 
                            type="text" 
                            placeholder="Search projects..." 
                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" 
                            value={searchQuery} 
                            onChange={(e) => setSearchQuery(e.target.value)} 
                        />
                    </div>
                </div>
                
                <div className="flex gap-2 overflow-x-auto pb-4 mb-4 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
                    {categories.map(cat => (
                        <button 
                            key={cat} 
                            onClick={() => setSelectedCategory(cat)} 
                            className={`px-4 py-2 rounded-full text-xs md:text-sm font-semibold whitespace-nowrap transition-all ${
                                selectedCategory === cat 
                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25' 
                                    : 'bg-white text-slate-600 border border-slate-200'
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredProjects.map((project) => {
                        const realData = getProjectData(project.id);
                        const isLiked = !!localUserLikes[project.id];
                        
                        return (
                            <div key={project.id} className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full relative">
                                <a 
                                    href={project.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    onClick={() => handleView(project.id)} 
                                    className="block h-full flex flex-col"
                                >
                                    <div className="p-1">
                                        <div className="bg-slate-100 rounded-xl h-40 md:h-48 relative overflow-hidden">
                                            <img 
                                                src={project.image} 
                                                alt={project.title} 
                                                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" 
                                                loading="lazy" 
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="p-5 flex flex-col flex-1">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
                                                {project.category}
                                            </span>
                                            <ExternalLink size={14} className="text-slate-300 group-hover:text-blue-600 transition-colors" />
                                        </div>
                                        <h3 className="font-bold text-base md:text-lg text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                                            {project.title}
                                        </h3>
                                        <p className="text-xs md:text-sm text-slate-500 leading-relaxed mb-4">
                                            {project.desc}
                                        </p>
                                    </div>
                                </a>
                                
                                <div className="px-5 pb-5 mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-1 text-slate-400">
                                            <Eye size={14} />
                                            <span className="text-xs font-semibold">{realData.views || 0}</span>
                                        </div>
                                        
                                        <button 
                                            onClick={(e) => handleLike(e, project.id)} 
                                            className={`flex items-center gap-1 px-2 py-1 rounded-full transition-all duration-200 ${
                                                isLiked 
                                                    ? 'bg-red-50 text-red-500' 
                                                    : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                                            }`}
                                        >
                                            <Heart size={14} fill={isLiked ? "currentColor" : "none"} className={isLiked ? "liked-anim" : ""} />
                                            <span className="text-xs font-semibold">{realData.likes || 0}</span>
                                        </button>
                                    </div>
                                    
                                    <button 
                                        onClick={(e) => { 
                                            e.preventDefault(); 
                                            setActiveProjectForComments(project); 
                                        }} 
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-50 text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-colors text-xs font-bold"
                                    >
                                        <MessageSquare size={14} />
                                        {(realData.comments?.length || 0) > 0 ? `${realData.comments.length}` : 'Chat'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            
            <ProjectCommentsModal 
                isOpen={!!activeProjectForComments} 
                onClose={() => setActiveProjectForComments(null)} 
                project={activeProjectForComments} 
                liveData={liveData} 
                isAdmin={isAdmin}
                onAddComment={handleAddComment} 
                onLikeComment={handleLikeComment}
                onAddReply={handleAddReply}
                onDeleteComment={handleDeleteComment}
            />
        </section>
    );
}

export default Portfolio;